import type { AgeRange, ArtBook, ArtBookThemePage } from '../../definitions/ArtBook'
import type { Gender } from '../../definitions/Gender'
import { getFirebaseToken } from '../firebase'

export interface QueryBookResult {
  photoBookId: string
  createTime: number
  metadata: { toapis?: string | null; userInputImageUuid?: string; userInputImageUrl: string; userInputImagePath?: string; gender: Gender; name: string; ageRange: AgeRange; ageYears?: number | null; domain: string; themeId: string; themeVersion: string; themeName: string; themePageCount: number; themePreviewPageCount: number; characterStyleId: string; characterStyleVersion: string; characterStyleName: string; requiresCharacterAnchor: boolean; characterAnchorReady: boolean }
  cover: ArtBook['cover']
  pages: Array<Omit<ArtBook['pages'][number], 'generateState'>>
  revision: number
}

export interface CreateBookPayload {
  themeId: string
  themeVersion?: string
  characterStyleId?: string
  characterStyleVersion?: string
  name: string
  gender: Gender
  ageRange: AgeRange
  referenceImageUuid: string
  referenceImagePath: string
  toapis?: string
  domain: string
  customerEmail?: string
}

export interface CustomizeBookPayload {
  photoBookId: string
  coverGenerationUuid?: string
  excludedPageNumbers: number[]
  revision?: number
}

export interface BookThemeSummary {
  id: string
  version: string
  name: string
  description: string
  language: string
  displayOrder: number
  accentColor: string
  coverTitleTemplate: string
  previewPageCount: number
  pageCount: number
  personalized: boolean
  supportsCharacterStyles: boolean
  availableCharacterStyleIds: string[]
  characterStyleId: string
  requiresCharacterAnchor: boolean
  tags: string[]
  pages?: ArtBookThemePage[]
}

export interface CharacterStyleSummary {
  id: string
  version: string
  category: string
  name: string
  description: string
  displayOrder: number
  hasStyleReference: boolean
}

export type CatalogSampleCharacterId = 'lily' | 'oliver'

export interface CatalogSampleResult {
  bookId: string
  manifestVersion: string
  themeVersion: string
  characterStyleId: string
  characterStyleVersion: string
  character: { id: CatalogSampleCharacterId; name: string; gender: 'GIRL' | 'BOY' }
  coverUrl: string
  pages: Array<{ pageNumber: number; pageId: string; title: string; imageUrl: string }>
  expiresAt: string
}

const CATALOG_SAMPLE_CACHE_PREFIX = 'luciesbook.catalog-sample.v1'
const CATALOG_SAMPLE_EXPIRY_MARGIN_MS = 10 * 60 * 1000

function catalogSampleCacheKey(bookId: string, characterId: CatalogSampleCharacterId): string {
  return `${CATALOG_SAMPLE_CACHE_PREFIX}:${bookId}:${characterId}`
}

function readCachedCatalogSample(bookId: string, characterId: CatalogSampleCharacterId): CatalogSampleResult | undefined {
  try {
    const value = window.localStorage.getItem(catalogSampleCacheKey(bookId, characterId))
    if (!value) return undefined
    const cached = JSON.parse(value) as CatalogSampleResult
    if (!cached.manifestVersion || !cached.expiresAt || cached.bookId !== bookId || cached.character.id !== characterId || cached.pages.length !== 26) return undefined
    return cached
  } catch {
    return undefined
  }
}

function cacheCatalogSample(sample: CatalogSampleResult): void {
  try {
    window.localStorage.setItem(catalogSampleCacheKey(sample.bookId, sample.character.id), JSON.stringify(sample))
  } catch {
    // Storage can be disabled or full; the request still succeeds without it.
  }
}

function hasReusableCatalogUrls(sample?: CatalogSampleResult): sample is CatalogSampleResult {
  return Boolean(sample && Date.parse(sample.expiresAt) - Date.now() > CATALOG_SAMPLE_EXPIRY_MARGIN_MS)
}

export interface CustomerOrderResult {
  orderId: string
  photoBookId: string
  checkoutStatus: string | null
  paymentStatus: string | null
  isPaid: boolean
  orderStatus: string | null
  gelatoOrderStatus: string | null
  createTime: number | null
}

export class BookService {
  constructor(private baseUrl: string) {}
  private async request<T>(path: string, payload: unknown): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${await getFirebaseToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!response.ok) throw new Error(`API request failed (${response.status})`)
    return response.json() as Promise<T>
  }
  private async get<T>(path: string): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`)
    if (!response.ok) throw new Error(`API request failed (${response.status})`)
    return response.json() as Promise<T>
  }
  listThemes() { return this.get<{ themes: BookThemeSummary[]; characterStyles: CharacterStyleSummary[] }>('/api/book-themes') }
  async getCatalogSample(bookId: string, characterId: CatalogSampleCharacterId, options: { forceRefresh?: boolean } = {}) {
    const cached = readCachedCatalogSample(bookId, characterId)
    const reusable = hasReusableCatalogUrls(cached)
    const headers: Record<string, string> = {}
    if (reusable && !options.forceRefresh) headers['If-None-Match'] = `"${cached.manifestVersion}"`
    try {
      const response = await fetch(`${this.baseUrl}/api/catalog-samples/${encodeURIComponent(bookId)}/${characterId}`, { headers, cache: 'no-store' })
      if (response.status === 304 && reusable) return cached
      if (!response.ok) throw new Error(`API request failed (${response.status})`)
      const sample = await response.json() as CatalogSampleResult
      if (!options.forceRefresh && reusable && sample.manifestVersion === cached.manifestVersion) return cached
      cacheCatalogSample(sample)
      return sample
    } catch (error) {
      if (reusable && !options.forceRefresh) return cached
      throw error
    }
  }
  createBook(payload: CreateBookPayload) { return this.request<{ photoBookId: string; theme: BookThemeSummary; characterStyle: CharacterStyleSummary; totalPageCount: number; revision: number }>('/api/photo-book/create', payload) }
  updateBook(payload: CustomizeBookPayload) { return this.request<{ photoBookId: string; revision: number }>('/api/photo-book/update', payload) }
  queryBook(payload: { photoBookId: string }) { return this.request<QueryBookResult>('/api/photo-book/query', payload) }
  checkout(payload: { photoBookId: string; successUrl: string; cancelUrl: string }) { return this.request<{ orderId: string; sessionId: string; hostUrl: string }>('/api/checkout/create-session', payload) }
  queryOrder(payload: { orderId: string; checkoutSessionId?: string }) { return this.request<CustomerOrderResult>('/api/order/query', payload) }
}

export const bookService = new BookService(import.meta.env.VITE_API_URL)
